import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useMaterialSearchStore } from '$/components/core/Collection/MaterialSearch/store/useMaterialSearchStore';
import { type MaterialType } from '$/services/usecases/materials/mapper/materials';
import { infiniteMaterialsQuery } from '$/services/usecases/materials/queries';
import { useCollectionStore } from '$/stores/useCollectionStore';

export type UseInfiniteMaterialsPayload = {
  type?: MaterialType;
  limit?: number;
  hideProMaterials?: boolean;
};

export const useInfiniteMaterials = (opt: UseInfiniteMaterialsPayload = {}) => {
  const brightnessFilter = useCollectionStore.useBrightnessFilter();
  const filterOptions = useCollectionStore.useAppliedFilterOptions();
  const query = useMaterialSearchStore.useQuery();

  const [showSkeleton, setShowSkeleton] = useState(false);

  const hasCollectionFilter =
    opt.type === 'color_tint' &&
    filterOptions.some((option) => option.option === 'collection');

  const queryRes = useInfiniteQuery(
    infiniteMaterialsQuery({
      limit: opt.limit ?? 100,
      filter: filterOptions,
      brightnessFilter,
      type: opt.type ?? 'color_tint',
      query: query,
      order: hasCollectionFilter ? 'page asc, pagePosition asc' : undefined,
    }),
  );

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (queryRes.isLoading) {
      timer = setTimeout(() => setShowSkeleton(true), 500);
    } else {
      setShowSkeleton(false);
    }
    return () => clearTimeout(timer);
  }, [queryRes.isLoading]);

  const count = queryRes.data?.pages?.[0]?.count ?? 0;

  return [
    queryRes,
    showSkeleton,
    queryRes.data?.pages.flatMap((page) => page.results),
    count,
  ] as const;
};
